import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import { Drawer, List, ListItem, ListItemButton, ListItemText, IconButton, Typography, ListItemIcon, Badge } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeIcon from '@mui/icons-material/Home';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import GroupIcon from '@mui/icons-material/Group';
import ArticleIcon from '@mui/icons-material/Article';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import SportsIcon from '@mui/icons-material/Sports';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SecurityIcon from '@mui/icons-material/Security';
import Divider from '@mui/material/Divider';
import { useAppSelector } from '../../../app/hooks'
import { useLoadLeagueData, useLoadTradeData, useLoadPlayerClaimData, useUpdateTradeDataFromMessage, useUpdatePlayerClaimDataFromMessage, useUpdatePlayerPoachDataFromMessage, useUpdateLeagueDataFromMessage } from '../../../app/dataLoaderHooks';
import { League, LeaguePhase, TradeProposal, TeamPlayerClaims, PlayerPoachInfo } from '../../../sdk/model';
import { Properties } from '../../../Properties';
import useWebSocketListener from '../../web-sockets/WebSocketListener';

const ACTIVE_LEAGUE_PHASES = new Set(['REGULAR_SEASON', 'PLAYOFFS', 'SEASON_COMPLETE'])

const NAV_CTRL_BUTTONS = new Set(['Meta', 'Control', 'Shift'])

function LeagueMenu({ onSideNavOpenChange }) {
  const {leagueId} = useParams();

  // Navigator/Dispatch
  const navigate = useNavigate();

  // State  
  const user = useAppSelector((state) => state.user);
  const [sideNavOpen, setSideNavOpen] = React.useState(false);

  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  
  // Team-specific data state
  const [isTradeLoadComplete, setTradeLoadComplete] = React.useState(false);
  const [isPlayerClaimLoadComplete, setPlayerClaimLoadComplete] = React.useState(false);
  
  const [ctrlActive, setCtrlActive] = React.useState(false);

  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicDraftData: true,
                                                              loadFullDraftData: false, loadContractData: false,
                                                              loadBasicAuctionData: true, loadFullAuctionData: false,
                                                              loadRosterData: false, loadRosterFor: [],
                                                              loadWaivedContracts: false, loadScheduleData: false,
                                                              isException: isException, setIsException: setIsException,
                                                              isLoadComplete: isLoadComplete, setLoadComplete: setLoadComplete});

  // Get user's team (calculate early for hook dependencies)
  const userTeam = React.useMemo(() => 
    Object.values(league?.teams || {})
      .find(team => team?.owners?.some(owner => owner.id === user.id)),
    [league?.teams, user.id]
  );
  
  // Team-specific data loading (must be called unconditionally)
  const trades: Array<TradeProposal> = useLoadTradeData({
    teamId: userTeam?.id || '',
    setIsException: setIsException,
    isLoadComplete: isTradeLoadComplete,
    setLoadComplete: setTradeLoadComplete
  });
  
  const playerClaims: Array<TeamPlayerClaims> = useLoadPlayerClaimData({
    teamId: userTeam?.id,
    setIsException: setIsException,
    isLoadComplete: isPlayerClaimLoadComplete,
    setLoadComplete: setPlayerClaimLoadComplete
  });
  
  const playerPoachInfo: Array<PlayerPoachInfo> | undefined = useAppSelector(
    (state) => state.playerPoachDataLoad
  )[userTeam?.id || ''];
  
  // WebSocket listeners (must be called unconditionally)
  const leagueWebSocketUrl = Properties.websocketsUrl + '/ws/league/' + user.username + '/' + leagueId + '/' + (league?.phase || '');
  useWebSocketListener(leagueWebSocketUrl, useUpdateLeagueDataFromMessage);
  
  const tradeWebSocketUrl = userTeam ? Properties.websocketsUrl + '/ws/trades/' + user.username + '/' + (userTeam.id || '') : null;
  useWebSocketListener(tradeWebSocketUrl, useUpdateTradeDataFromMessage, [userTeam?.id || '']);
  
  const claimsWebSocketUrl = userTeam ? Properties.websocketsUrl + '/ws/playerClaims/' + user.username + '/' + (userTeam?.id || '') : null;
  useWebSocketListener(claimsWebSocketUrl, useUpdatePlayerClaimDataFromMessage, [userTeam?.id || '']);
  
  const poachWebSocketUrl = userTeam ? Properties.websocketsUrl + '/ws/playerPoachRequests/' + user.username + '/' + (userTeam?.id || '') : null;
  useWebSocketListener(poachWebSocketUrl, useUpdatePlayerPoachDataFromMessage, [userTeam?.id || '']);
  
  // Early returns after all hooks have been called
  if(isException) {
    return <Typography color="red">There was an error loading the league</Typography>
  } else if(!isLoadComplete) {
    return <Typography color="white">Loading league data..</Typography>
  }
  
  if (!league) {
    return <p>Loading...</p>
  }
  
  const handleKeyPress = e => {
    if (NAV_CTRL_BUTTONS.has(e.key)) {
      setCtrlActive(true)
    }
  }
  const handleKeyRelease = e => {
    if (NAV_CTRL_BUTTONS.has(e.key)) {
      setCtrlActive(false)
    }
  }
  
  // Computed values (can be calculated after early returns are done)
  const commish = league?.commissioners?.some(commish => commish.id === user.id);
  const teamOwner = Object.entries(league!.teams)
              .some(([teamIndex, team]) => team?.owners?.some(owner => owner.id === user.id));
  
  const pendingTrades = trades.filter(trade => trade.status === 'PENDING').length;
  
  // Dynamic context based on day of week (same logic as LeagueHome)
  const currentDayOfWeek = new Date().getDay();
  const isMatchupDays = [0, 1, 4, 5, 6].includes(currentDayOfWeek); // Thu-Mon
  const isWaiverDays = [2, 3].includes(currentDayOfWeek); // Tue-Wed
  function toggleSideNav() {
  setSideNavOpen(!sideNavOpen);
  }
  const drawerWidth = 250;

  const navigateAndClose = (path: string) => {
    navigate(path);
    toggleSideNav();
  };

  // Helper component for navigation items
  const NavItem = ({ 
    icon, 
    text, 
    path, 
    highlight = false, 
    badge = false,
    disabled = false 
  }: {
    icon: React.ReactNode;
    text: string;
    path: string;
    highlight?: boolean;
    badge?: boolean;
    disabled?: boolean;
  }) => (
    <ListItem disablePadding>
      <ListItemButton 
        href={ctrlActive ? path : ""}
        onClick={ctrlActive ? undefined : () => navigateAndClose(path)}
        disabled={disabled}
        sx={{
          ...(highlight && {
            backgroundColor: 'primary.light',
            '&:hover': {
              backgroundColor: 'primary.main',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
            '& .MuiListItemText-primary': {
              color: 'primary.contrastText',
              fontWeight: 'bold',
            }
          })
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {badge ? (
            <Badge 
              badgeContent="!" 
              color="error" 
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  right: 3,
                  top: 3,
                }
              }}
            >
              {icon}
            </Badge>
          ) : (
            icon
          )}
        </ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );

  return <>
    <IconButton
        color="inherit"
        aria-label="Open League Menu"
        onClick={toggleSideNav}
        edge="start"
        sx={{ 
          position: 'fixed',
          top: '10px',
          left: '0',
          zIndex: 1200,
          padding: '16px',
          borderRadius: '0',
          '& .MuiSvgIcon-root': {
            fontSize: '2rem'
          },
          ...(sideNavOpen && { display: 'none' })
        }}>
      <MenuIcon />
    </IconButton>
    <Drawer
      sx={{
        position: 'fixed',
        '& .MuiDrawer-paper': {
          position: 'fixed',
          width: drawerWidth,
          boxSizing: 'border-box',
          zIndex: 1300,
          top: 70, // Height of the AppBar
          height: 'calc(100vh - 64px)', // Adjust height to account for AppBar
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
          overflowX: 'hidden', // Hide horizontal scrollbar
        },
      }}
      onKeyDown={handleKeyPress}
      onKeyUp={handleKeyRelease}
      anchor='left'
      variant="temporary"
      open={sideNavOpen}
      onClose={toggleSideNav}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}>
      <IconButton onClick={toggleSideNav}>
        <ChevronLeftIcon />
        <Typography
            variant="body2"
            align="center"
            color="text.primary"
            gutterBottom>
          League Menu
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
        ({league!.phase})
      </Typography>
      </IconButton>
      <Divider />
      <Box sx={{ width: drawerWidth }}>
        {/* Priority Actions Section */}
        <List>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              Quick Actions
            </Typography>
          </ListItem>
          
          <NavItem 
            icon={<HomeIcon />} 
            text="Home" 
            path={`/league/${league.id}`} 
          />
          
          {ACTIVE_LEAGUE_PHASES.has(league.phase!) && (
            <NavItem 
              icon={<SportsMmaIcon />} 
              text="Current Matchup" 
              path={`/league/${league.id}/matchup`} 
              highlight={isMatchupDays && teamOwner}
              badge={isMatchupDays && teamOwner}
            />
          )}
          
          {ACTIVE_LEAGUE_PHASES.has(league.phase!) && (
            <NavItem 
              icon={<CalendarTodayIcon />} 
              text="Current Week" 
              path={`/league/${league.id}/week`} 
              highlight={isMatchupDays}
            />
          )}
        </List>

        <Divider />
        
        {/* Team Info Section */}
        {userTeam && (
          <>
            <List>
              <ListItem>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Team Info
                </Typography>
              </ListItem>
              
              <NavItem 
                icon={<SportsIcon />} 
                text={userTeam.name || "My Team"} 
                path={`/league/${league.id}/team/${userTeam.id}`} 
              />
              
              <NavItem 
                icon={<SwapHorizIcon />} 
                text="My Trades" 
                path={`/league/${league.id}/team/${userTeam.id}/trades`} 
                badge={pendingTrades > 0}
                highlight={pendingTrades > 0}
              />
              
              <NavItem 
                icon={<GroupIcon />} 
                text="Player Claims" 
                path={`/league/${league.id}/team/${userTeam.id}/playerClaims`} 
                badge={playerClaims.reduce((sum, teamClaims) => 
                  sum + (teamClaims.waiverClaims?.length || 0) + (teamClaims.faabClaims?.length || 0), 0) > 0}
                highlight={isWaiverDays}
              />
              
              {league.phase === 'OFFSEASON' && league.totalSeasons! > 1 && 
                <NavItem 
                  icon={<RequestQuoteIcon />} 
                  text="Franchise/5th-Year Tags" 
                  path={`/league/${league.id}/team/${userTeam.id}/contractTags`} 
                />
              }
              
              {league.currentSeason?.currentWeek !== undefined && 
               league.currentSeason?.currentWeek >= 5 && 
               league.currentSeason?.currentWeek <= 14 && (
                <NavItem 
                  icon={<RequestQuoteIcon />} 
                  text="Mid-Season Extensions" 
                  path={`/league/${league.id}/team/${userTeam.id}/midSeasonExtensions`} 
                />
              )}
              
              {playerPoachInfo !== undefined && playerPoachInfo.length > 0 && (
                <NavItem 
                  icon={<SecurityIcon />} 
                  text="Active Poach Attempts" 
                  path={`/league/${league.id}/team/${userTeam.id}/poachOffers`} 
                  badge={true}
                  highlight={true}
                />
              )}
              
              <NavItem 
                icon={<SettingsIcon />} 
                text="Team Settings" 
                path={`/league/${league.id}/team/${userTeam.id}/settings`} 
              />
            </List>
            
            <Divider />
          </>
        )}
        
        {/* Roster Management Section */}
        <List>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              Roster Management
            </Typography>
          </ListItem>
          
          <NavItem 
            icon={<SwapHorizIcon />} 
            text="Trade Builder" 
            path={`/league/${league.id}/trade`} 
            highlight={isWaiverDays}
            badge={isWaiverDays}
          />
          
          <NavItem 
            icon={<GroupIcon />} 
            text="Players & Waivers" 
            path={`/league/${league.id}/players`} 
            highlight={isWaiverDays}
            badge={isWaiverDays}
          />
          
          <NavItem 
            icon={<ArticleIcon />} 
            text="Contracts" 
            path={`/league/${league.id}/contracts`} 
          />
          
          <NavItem 
            icon={<EventIcon />} 
            text="Draft Picks" 
            path={`/league/${league.id}/picks`} 
          />
        </List>

        <Divider />
        
        {/* League Information Section */}
        <List>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              League Info
            </Typography>
          </ListItem>
          
          <NavItem 
            icon={<RequestQuoteIcon />} 
            text="Roster & Cap Space" 
            path={`/league/${league.id}/roster-cap`} 
          />
          
          <NavItem 
            icon={<CalendarTodayIcon />} 
            text="Schedule" 
            path={`/league/${league.id}/schedule`} 
          />
          
          <NavItem 
            icon={<HistoryIcon />} 
            text="Transactions" 
            path={`/league/${league.id}/transactions`} 
          />
          
          <NavItem 
            icon={<TrendingUpIcon />} 
            text="Power Rankings" 
            path={`/league/${league.id}/powerRankings`} 
          />
          
          <NavItem 
            icon={<SettingsIcon />} 
            text="League Settings" 
            path={`/league/${league.id}/settings`} 
          />
        </List>
        
        <Divider />
        
        {/* History Section */}
        <List>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              History
            </Typography>
          </ListItem>
          
          <NavItem 
            icon={<HistoryIcon />} 
            text="Draft History" 
            path={`/league/${league.id}/draftHistory`} 
          />
          
          <NavItem 
            icon={<HistoryIcon />} 
            text="Auction History" 
            path={`/league/${league.id}/auctionHistory`} 
          />
          
          <NavItem 
            icon={<HistoryIcon />} 
            text="League History" 
            path={`/league/${league.id}/history`} 
          />
        </List>
        
        {/* Commissioner Tools Section */}
        {commish && (
          <>
            <Divider />
            <List>
              <ListItem>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Commissioner
                </Typography>
              </ListItem>
              
              <NavItem 
                icon={<AdminPanelSettingsIcon />} 
                text="Commissioner Tools" 
                path={`/league/${league.id}/settings?commish=true`} 
                highlight={true}
              />
            </List>
          </>
        )}
      </Box>
    </Drawer>
  </>;
}

export default LeagueMenu;